import { useTranslation } from 'react-i18next';
import { Grid, Box, Button, Paper, Typography, Divider, alpha, Alert, AlertTitle } from '@mui/material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { CloudUpload, Replay, Save } from '@mui/icons-material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { ImportMemberExcelHeaderEnum } from 'src/shared/models/enums/importMemberExcelHeaders.enum';
import { importMembers } from 'src/shared/services/formMemberService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { ImportMembers } from '../models/importMembers';
import { MemberImportCancelButton } from '../components/memberImportCancelButton';
import { MemberImportDownloadButton } from '../components/memberImportDownloadButton';

export interface ExcelRow {
  [key: string]: string | number;
}

export interface HeaderError {
  index: number;
  hasError: boolean;
}

export const MemberImportPage = () => {
  const { t } = useTranslation(['member']);
  const [loading, setLoading] = useState(false);
  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();

  const isMounted = useIsMounted();

  const [tableHeaders, setTableHeaders] = useState<string[]>();
  const [gridColumns, setGridColumns] = useState<GridColDef[]>([]);
  const [headerErrors, setHeaderErrors] = useState<HeaderError[]>([]);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [displayData, setDisplayData] = useState<ExcelRow[]>();
  const [rawData, setRawData] = useState<string[]>([]);
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<Blob>();
  const [fileName, setFileName] = useState<string>();
  const [, setFileDataURL] = useState<string | null | ArrayBuffer>(null);
  const [importCompleted, setImportCompleted] = useState<boolean>(false);

  const validateHeaders = (headers: string[]) => {
    const tempErrors: HeaderError[] = [];
    headers.forEach((item, ix1) => {
      if (!Object.values(ImportMemberExcelHeaderEnum).includes(item as ImportMemberExcelHeaderEnum)) {
        tempErrors.push({ index: ix1, hasError: true });
      }
    });
    setHeaderErrors(tempErrors);
    return tempErrors;
  };

  const convertToJson = (csv) => {
    const lines = csv.split('\n');

    const result: ExcelRow[] = [];
    const tempRawData: string[] = [];

    const headers = lines[0].split(',');
    setTableHeaders(headers);

    const nrOfHeaderErrors = validateHeaders(headers);
    if (nrOfHeaderErrors.length > 0) {
      setErrorCount(nrOfHeaderErrors.length);
      ConsoleHelper.log('nrOfHeaderErrors', nrOfHeaderErrors);
    }

    const createGridColumns: GridColDef[] = [];
    headers.forEach((x, index) => {
      const hasError = nrOfHeaderErrors.find((y) => y.index === index);
      createGridColumns.push({
        field: x,
        headerName: hasError ? x : t(`member:tableHeaders.${x}`),
        headerClassName: hasError ? 'header-error' : undefined,
      });
    });
    ConsoleHelper.log('createGridColumns', createGridColumns);
    setGridColumns(createGridColumns);

    for (let i = 1; i < lines.length; i += 1) {
      const obj = {};
      const currentline = lines[i].split(',');

      // Ignore empty rows
      if (currentline.join('') !== '') {
        for (let j = 0; j < headers.length; j += 1) {
          obj[headers[j]] = currentline[j];
        }
        // eslint-disable-next-line dot-notation
        obj['id'] = i; // Add id

        result.push(obj);
        tempRawData.push(currentline.join(';'));
      }
    }
    setRawData(tempRawData);
    setDisplayData(result.slice(1));

    // return result; //JavaScript object
    return JSON.stringify(result); // JSON
  };

  useEffect(() => {
    let fileReader: FileReader;
    let isCancel = false;
    if (file) {
      setLoading(true);
      fileReader = new FileReader();
      fileReader.onload = (e) => {
        const { result } = fileReader;
        if (result && !isCancel) {
          setFileDataURL(result);
          const wb = XLSX.read(result, { type: 'binary' });
          /* Get first worksheet */
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          /* Convert array of arrays */
          const data = XLSX.utils.sheet_to_csv(ws, {});
          /* Update state */
          // ConsoleHelper.log(`Data>>>${data}`); // shows that excel data is read
          ConsoleHelper.log(convertToJson(data)); // shows data in json format
        }
      };
      // fileReader.readAsDataURL(file);
      fileReader.readAsBinaryString(file);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
    return () => {
      isCancel = true;
      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    };
  }, [file]);

  const changeHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    ConsoleHelper.log('changeHandler');
    e.stopPropagation();
    e.preventDefault();
    if (e.target.files) {
      const newFile = e.target.files[0];
      ConsoleHelper.log('newFile', newFile);
      setFile(newFile);
      setFileName(newFile.name);
    }
  };

  const onSelectFile = () => {
    if (fileUploadRef && fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const postImportToServer = async () => {
    setLoading(true);
    try {
      if (displayData && tableHeaders) {
        const importData: ImportMembers = {
          data: rawData,
          columnNames: tableHeaders.join(','),
          associationId: association?.localAss_id || 0,
          districtId: district?.countyAss_id || association?.countyAss_id || 0,
          fileName: fileName || 'unknown',
        };
        await importMembers(importData);
        if (isMounted()) {
          toast.success(<>{t('member:importMember.importSuccessMessage')}</>);
          setImportCompleted(true);
        }
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.error(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const resetFile = () => {
    setFile(undefined);
    setDisplayData(undefined);
    setTableHeaders(undefined);
    setErrorCount(0);
    setHeaderErrors([]);
  };

  const cancelImport = (confirmCancel: boolean) => {
    if (confirmCancel) {
      resetFile();
    } else {
      setImportCompleted(false);
      resetFile();
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <MainContentHeader title={t('member:importMember.header')} />
      </Grid>
      <Grid item xs={12}>
        <FormLoaderWrapper loading={loading}>
          <Paper
            sx={{
              p: 2,
              '& .errorBox': {
                p: 0.5,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #f00 !important',
                color: 'red',
                backgroundColor: '#fffbd5',
                fontWeight: 600,
              },
            }}
          >
            {!displayData && !tableHeaders && (
              <Grid container spacing={2}>
                <AssociationAndDistrictSelect
                  useGridLayout
                  hideDistrict={false}
                  association={association}
                  district={district}
                  associationCallback={(val) => setAssociation(val || undefined)}
                  districtCallback={(val) => setDistrict(val || undefined)}
                />
                <Grid item xs={12}>
                  <Divider sx={{ mx: -2, mb: 2 }} />
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2}>
              {!association && (
                <Grid item xs={12}>
                  <Typography variant='h6'>Välj förening</Typography>
                  <Typography variant='body2'>
                    För att kunna ladda upp en importfil måste du först välja en förening som importen skall tillhöra.
                  </Typography>
                </Grid>
              )}
              {!displayData && (
                <>
                  <Grid item xs={6}>
                    <Button variant='contained' disableElevation size='large' onClick={onSelectFile} fullWidth disabled={!association}>
                      <CloudUpload sx={{ mr: 1 }} />
                      <span>{t('member:importFile.selectFile')}</span>
                    </Button>
                    <input id='upload-file' name='upload-file' type='file' style={{ display: 'none' }} ref={fileUploadRef} onChange={changeHandler} />
                  </Grid>
                  <Grid item xs={6}>
                    <MemberImportDownloadButton />
                  </Grid>
                </>
              )}

              {displayData && tableHeaders && !importCompleted && (
                <Grid item xs={12}>
                  <Typography gutterBottom variant='h6'>
                    {t('member:importMember.previewHeader')} <small>{`(${displayData.length} ${t('member.importMember.rowsSuffix')})`}</small>
                  </Typography>
                  {(errorCount > 0 || headerErrors.length > 0) && (
                    <Alert severity='error' variant='filled' sx={{ mb: 2 }}>
                      <AlertTitle>
                        <strong>Importen misslyckades, {errorCount} felaktiga värden</strong>
                      </AlertTitle>
                      Kontrollera rödmarkerade fält.
                    </Alert>
                  )}
                  <Box
                    sx={{
                      height: 500,
                      width: '100%',
                      '& .header-error': {
                        color: (thm) => thm.palette.error.main,
                        backgroundColor: (thm) => alpha(thm.palette.warning.light, 0.3),
                      },
                    }}
                  >
                    <DataGrid
                      rowHeight={35}
                      rows={displayData}
                      columns={gridColumns}
                      pagination
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 50, page: 0 },
                        },
                      }}
                      pageSizeOptions={[50]}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Grid>
              )}

              {association && displayData && !importCompleted && errorCount === 0 && (
                <Grid item xs={12}>
                  <Alert severity='success' variant='outlined' sx={{ backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1) }}>
                    <AlertTitle>
                      <strong>Inga fel hittades</strong>
                    </AlertTitle>
                    Nu kan du spara filen.
                  </Alert>
                </Grid>
              )}
              {displayData && tableHeaders && !importCompleted && (
                <Grid item xs={12}>
                  <Button
                    size='large'
                    variant='contained'
                    color='success'
                    disableElevation
                    disabled={errorCount > 0}
                    startIcon={<Save />}
                    onClick={() => postImportToServer()}
                  >
                    {t('common:button.save')}
                  </Button>
                  <MemberImportCancelButton callback={() => cancelImport(true)} useConfirm={errorCount === 0} />
                </Grid>
              )}

              {association && importCompleted && (
                <Grid item xs={12}>
                  <Alert severity='success' variant='filled' sx={{ mb: 2 }}>
                    <AlertTitle sx={{ mb: 0 }}>
                      <strong>{t('member:importMember.importSuccessMessage')}</strong>
                    </AlertTitle>
                  </Alert>
                  <Button size='large' variant='outlined' onClick={() => cancelImport(false)} startIcon={<Replay />}>
                    {t('common:button.startOver')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        </FormLoaderWrapper>
      </Grid>
    </Grid>
  );
};
